import { Box, IconButton, Typography } from '@mui/material';
import { toast } from 'sonner';

import { CheckGlyph, CloseGlyph } from '../icons';
import useStyles from './sidekickToast.styles';

const TOAST_DURATION_MS = 6000;
const ERROR_TOAST_DURATION_MS = 8000;
const TOAST_ICON_SIZE_PX = 16;

/**
 * Inline 16x16 error glyph (rounded square with a centered `!`) used
 * by the failure toast. Mirrors the Figma `Icon/error` outline shape
 * from node `I16166:21122;14448:14528`.
 */
function ErrorGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect
        x="1.4"
        y="1.4"
        width="13.2"
        height="13.2"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 4.5v4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="8" cy="11.25" r="0.85" fill="currentColor" />
    </svg>
  );
}

type SidekickToastProps = {
  id: number | string;
  variant: 'success' | 'error';
  layout: 'default' | 'artifact';
  message: string;
  closeAriaLabel: string;
  testId: string;
};

/**
 * Inline Sidekick toast body. `role` is bound to the variant so the
 * success confirmation goes into a polite live region (`status` —
 * queues behind the screen reader's current utterance) while the
 * error variant uses the assertive `alert` role and interrupts.
 * Matches sonner's built-in role mapping.
 */
function SidekickToast({
  id,
  variant,
  layout,
  message,
  closeAriaLabel,
  testId,
}: SidekickToastProps) {
  const { classes, cx } = useStyles();
  const role = variant === 'error' ? 'alert' : 'status';

  return (
    <Box
      className={cx(
        classes.toast,
        layout === 'artifact' && classes.artifactToast
      )}
      role={role}
      data-testid={testId}
    >
      <Box className={classes.message}>
        <Box className={classes.iconBox}>
          {variant === 'success' ? (
            <CheckGlyph size={TOAST_ICON_SIZE_PX} />
          ) : (
            <ErrorGlyph />
          )}
        </Box>
        <Typography variant="body_medium" className={classes.label}>
          {message}
        </Typography>
      </Box>
      <IconButton
        className={classes.closeButton}
        onClick={() => toast.dismiss(id)}
        aria-label={closeAriaLabel}
        data-testid={`${testId}-close`}
      >
        <CloseGlyph size={TOAST_ICON_SIZE_PX} />
      </IconButton>
    </Box>
  );
}

/** Arguments shared by both Sidekick toast variants. */
export type SidekickToastOptions = {
  /** Visible toast copy. */
  message: string;
  /** Accessible label for the trailing dismiss control. */
  closeAriaLabel: string;
  /**
   * `data-testid` for the toast body; the close button derives
   * `${testId}-close`. Required so each surface names its own toast
   * rather than inheriting another surface's identifier.
   */
  testId: string;
  /** Uses the compact, content-width artifact-download toast treatment. */
  layout?: 'default' | 'artifact';
};

/**
 * Fire the Figma-styled success toast. Requires a `<Toaster />` (from
 * `@brightdrop/bd-ui` or any other `sonner` consumer) to be mounted
 * somewhere in the host app's tree.
 */
export function showSidekickSuccessToast({
  message,
  closeAriaLabel,
  testId,
  layout = 'default',
}: SidekickToastOptions): void {
  toast.custom(
    (id) => (
      <SidekickToast
        id={id}
        variant="success"
        layout={layout}
        message={message}
        closeAriaLabel={closeAriaLabel}
        testId={testId}
      />
    ),
    { duration: TOAST_DURATION_MS }
  );
}

/** Fire the Figma-styled error toast shown when an action fails. */
export function showSidekickErrorToast({
  message,
  closeAriaLabel,
  testId,
  layout = 'default',
}: SidekickToastOptions): void {
  toast.custom(
    (id) => (
      <SidekickToast
        id={id}
        variant="error"
        layout={layout}
        message={message}
        closeAriaLabel={closeAriaLabel}
        testId={testId}
      />
    ),
    { duration: ERROR_TOAST_DURATION_MS }
  );
}
